/**
 * PricingRuleDetailSidebar — bespoke right column for the custom Pricing Rule
 * detail page (StandardDetailPage's `renderRightSidebar`), Thai-first.
 *   • Trigger     — coupon-triggered vs automatic; surfaces the actual coupon(s)
 *   • Storefront  — where it shows (checkout / product page) + per-store scope
 *   • Validity    — campaign window + priority
 */

import { Link } from "react-router-dom";
import { Ticket, Store, CalendarClock, ChevronRight, ArrowRight, Power } from "lucide-react";
import { cn } from "@/lib/utils";
import { CARD } from "@/config/design-tokens";
import { useLanguage } from "@/i18n/useLanguage";
import { CardTitle, EmptyValue, formatReadableDate } from "@/features/customers/customer-detail/customerDetailPrimitives";
import { isCouponRule, type Lang } from "./pricingRuleDetailModel";
import { usePricingRuleCoupons, type LinkedCoupon } from "./usePricingRuleCoupons";
import { PricingRuleFieldSwitch } from "./PricingRuleFieldSwitch";
import { useDetailDensity, TEXT_LABEL } from "@/features/inventory/item-detail/hooks/useDetailDensity";

const truthy = (v: unknown): boolean => v === 1 || v === "1" || v === true;
const pick = (lang: Lang, th: string, en: string) => (lang === "th" ? th : en);

/** Usage line: "ใช้แล้ว 3/100" / "3 / 100 used". */
function couponUsage(c: LinkedCoupon, lang: Lang): string {
  const used = Number(c.used) || 0;
  const max = Number(c.maximum_use) || 0;
  if (max > 0) return pick(lang, `ใช้แล้ว ${used}/${max}`, `${used} / ${max} used`);
  return pick(lang, `ใช้แล้ว ${used} · ไม่จำกัด`, `${used} used · Unlimited`);
}

/** One tappable row that jumps straight to this specific coupon's page. */
function CouponRow({ coupon, lang }: { coupon: LinkedCoupon; lang: Lang }) {
  return (
    <Link
      to={`/promotions/${encodeURIComponent(coupon.coupon_code)}`}
      className="group flex items-center justify-between gap-3 rounded-lg border border-border bg-muted/30 px-3 py-2.5 transition-colors hover:border-sky-300 hover:bg-sky-50/60 dark:bg-white/[0.03] dark:hover:border-sky-500/30 dark:hover:bg-sky-500/10"
    >
      <span className="min-w-0">
        <span className="block truncate font-mono text-[13px] font-semibold tracking-wide text-gray-900 dark:text-gray-100">
          {coupon.coupon_code}
        </span>
        <span className="mt-0.5 block text-xs text-gray-500 dark:text-gray-400">{couponUsage(coupon, lang)}</span>
      </span>
      <ChevronRight className="h-4 w-4 shrink-0 text-gray-400 transition-colors group-hover:text-sky-600 dark:group-hover:text-sky-400" />
    </Link>
  );
}

/**
 * Master on/off switch — the promotion's live status. Writes the Pricing Rule
 * `disable` flag (inverted: switch on ⇒ disable 0). This is the true kill switch
 * for both coupon and automatic rules, and stays in lockstep with the status pill.
 */
function MasterSwitchRow({
  formData,
  docName,
  coupon,
  lang,
}: {
  formData: Record<string, any>;
  docName: string;
  coupon: boolean;
  lang: Lang;
}) {
  const live = !truthy(formData.disable);
  const title = coupon
    ? pick(lang, "เปิดใช้งานคูปอง", "Coupon live")
    : pick(lang, "เปิดใช้งานโปรโมชัน", "Promotion live");
  const sub = live
    ? pick(lang, "ลูกค้าใช้ได้บนหน้าร้านตอนนี้", "Customers can use it on your store")
    : pick(lang, "ปิดอยู่ — ลูกค้าใช้ไม่ได้", "Off — customers can’t use it");

  return (
    <div
      className={cn(
        "mb-4 flex items-center justify-between gap-3 rounded-xl border px-3.5 py-3 transition-colors",
        live
          ? "border-emerald-200 bg-emerald-50/60 dark:border-emerald-500/20 dark:bg-emerald-500/10"
          : "border-border bg-muted/30 dark:bg-white/[0.03]",
      )}
    >
      <div className="min-w-0">
        <div className="flex items-center gap-1.5 text-sm font-semibold text-gray-900 dark:text-gray-100">
          <Power className={cn("h-3.5 w-3.5 shrink-0", live ? "text-emerald-600 dark:text-emerald-400" : "text-gray-400")} />
          {title}
        </div>
        <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">{sub}</p>
      </div>
      <PricingRuleFieldSwitch
        docName={docName}
        field="disable"
        checked={live}
        invert
        ariaLabel={title}
        msgOn={pick(lang, "เปิดใช้งานแล้ว", "Turned on")}
        msgOff={pick(lang, "ปิดใช้งานแล้ว", "Turned off")}
      />
    </div>
  );
}

/** Trigger card — surfaces the actual coupon(s) linked to this rule. */
function TriggerCard({ formData, docName, lang }: { formData: Record<string, any>; docName: string; lang: Lang }) {
  const coupon = isCouponRule(formData);
  const { data: coupons, isLoading } = usePricingRuleCoupons(docName, coupon);

  return (
    <div className={cn(CARD, "p-5")}>
      <CardTitle icon={<Ticket className="h-3.5 w-3.5" />}>{pick(lang, "การเปิดใช้", "Trigger")}</CardTitle>

      {/* Master on/off — the promotion's live status, editable in place */}
      <MasterSwitchRow formData={formData} docName={docName} coupon={coupon} lang={lang} />

      {!coupon ? (
        <p className="text-[13px] text-gray-700 dark:text-gray-300">
          {lang === "th" ? (
            <>
              ใช้ <b>อัตโนมัติ</b> เมื่อตะกร้าเข้าเงื่อนไข — ไม่ต้องใช้โค้ด
            </>
          ) : (
            <>
              Applied <b>automatically</b> whenever the cart matches — no code needed.
            </>
          )}
        </p>
      ) : (
        <>
          <p className="mb-3 text-[13px] text-gray-700 dark:text-gray-300">
            {lang === "th" ? (
              <>
                ลูกค้ากรอก <b>โค้ดคูปอง</b> ตอนชำระเงินเพื่อรับสิทธิ์
              </>
            ) : (
              <>
                Redeemed with a <b>coupon code</b> the customer enters at checkout.
              </>
            )}
          </p>

          {isLoading ? (
            <div className="h-[52px] animate-pulse rounded-lg bg-muted/50 dark:bg-white/[0.04]" />
          ) : coupons && coupons.length > 0 ? (
            <div className="space-y-2">
              {coupons.map((c) => (
                <CouponRow key={c.name} coupon={c} lang={lang} />
              ))}
            </div>
          ) : (
            <p className="rounded-lg border border-dashed border-border px-3 py-2.5 text-[12px] text-gray-500 dark:text-gray-400">
              {pick(lang, "ยังไม่มีโค้ดคูปองผูกกับกฎนี้", "No coupon code is linked to this rule yet.")}
            </p>
          )}

          <Link
            to="/list/Coupon%20Code"
            className="mt-3 inline-flex items-center gap-1 text-[12px] font-medium text-gray-500 hover:text-sky-600 hover:underline dark:text-gray-400 dark:hover:text-sky-400"
          >
            {pick(lang, "โค้ดคูปองทั้งหมด", "All coupon codes")} <ArrowRight className="h-3 w-3" />
          </Link>
        </>
      )}
    </div>
  );
}

function KeyRow({ label, children }: { label: string; children: React.ReactNode }) {
  // Generic detail convention: spacing (rowPx/rowPy/rowGap) + label typography
  // (rowLabel) driven by the SAME useDetailDensity hook RowShell/InlineFieldRow use,
  // so this row breathes in lockstep with the main column across Compact/Spacious.
  const { rowLabel, rowPx, rowPy, rowGap } = useDetailDensity();
  return (
    <div className={cn("flex items-start justify-between", rowGap, rowPx, rowPy, rowLabel)}>
      <span className="text-gray-900 dark:text-gray-100">{label}</span>
      <span className="text-right font-medium text-gray-900 dark:text-gray-100">{children}</span>
    </div>
  );
}

/** A labelled row with a short hint and an inline, self-persisting switch. */
function SwitchRow({
  label,
  hint,
  docName,
  field,
  checked,
  msgOn,
  msgOff,
}: {
  label: string;
  hint: string;
  docName: string;
  field: string;
  checked: boolean;
  msgOn: string;
  msgOff: string;
}) {
  const { rowLabel, textSize, rowPx, rowPy, rowGap } = useDetailDensity();
  return (
    <div className={cn("flex items-center justify-between", rowGap, rowPx, rowPy)}>
      <div className="min-w-0">
        <div className={cn("text-gray-900 dark:text-gray-100", rowLabel)}>{label}</div>
        <p className={cn("mt-0.5 text-gray-500 dark:text-gray-400", TEXT_LABEL[textSize])}>{hint}</p>
      </div>
      <PricingRuleFieldSwitch
        docName={docName}
        field={field}
        checked={checked}
        ariaLabel={label}
        msgOn={msgOn}
        msgOff={msgOff}
        className="scale-90 origin-right"
      />
    </div>
  );
}

export function PricingRuleDetailSidebar({
  formData,
  docName,
}: {
  formData: Record<string, any>;
  docName: string;
}) {
  const { language: lang } = useLanguage();

  return (
    <div className="space-y-4">
      {/* Trigger — surfaces the specific coupon(s) linked to this rule */}
      <TriggerCard formData={formData} docName={docName} lang={lang} />

      {/* Storefront & coupon — the three visibility/trigger flags, editable in place */}
      <div className={cn(CARD, "p-5")}>
        <CardTitle icon={<Store className="h-3.5 w-3.5" />}>
          {pick(lang, "หน้าร้านและคูปอง", "Storefront & coupon")}
        </CardTitle>
        {/* -mx-5 = edge-to-edge like the main column's list: rowPx insets the content,
            dividers span the full card width. */}
        <div className="-mx-5 divide-y divide-border/40">
          <SwitchRow
            label={pick(lang, "ใช้โค้ดคูปอง", "Coupon code based")}
            hint={pick(lang, "ต้องกรอกโค้ดตอนชำระเงิน", "Requires a code at checkout")}
            docName={docName}
            field="coupon_code_based"
            checked={truthy(formData.coupon_code_based)}
            msgOn={pick(lang, "เปิดใช้โค้ดคูปองแล้ว", "Coupon code enabled")}
            msgOff={pick(lang, "เปลี่ยนเป็นอัตโนมัติแล้ว", "Switched to automatic")}
          />
          <SwitchRow
            label={pick(lang, "แสดงตอนชำระเงิน", "At checkout")}
            hint={pick(lang, "ใช้ส่วนลดได้ในหน้าชำระเงิน", "Usable on the checkout page")}
            docName={docName}
            field="storefront_enabled"
            checked={truthy(formData.storefront_enabled)}
            msgOn={pick(lang, "แสดงบนหน้าร้านแล้ว", "Shown on store")}
            msgOff={pick(lang, "ซ่อนจากหน้าร้านแล้ว", "Hidden from store")}
          />
          <SwitchRow
            label={pick(lang, "แสดงบนหน้าสินค้า", "On product page")}
            hint={pick(lang, "โชว์ป้ายโปรบนหน้าสินค้า", "Shows a promo badge on the product page")}
            docName={docName}
            field="storefront_show_on_detail"
            checked={truthy(formData.storefront_show_on_detail)}
            msgOn={pick(lang, "แสดงบนหน้าสินค้าแล้ว", "Shown on product page")}
            msgOff={pick(lang, "ซ่อนจากหน้าสินค้าแล้ว", "Hidden from product page")}
          />
          {/* Storefront scope now lives (editable) in the Promotion Summary card's
              "หน้าร้าน" row to avoid a duplicate control. */}
        </div>
      </div>

      {/* Validity */}
      <div className={cn(CARD, "p-5")}>
        <CardTitle icon={<CalendarClock className="h-3.5 w-3.5" />}>{pick(lang, "ระยะเวลา", "Validity")}</CardTitle>
        <div className="-mx-5 divide-y divide-border/40">
          {/* Starts/Ends now live (editable) in the Promotion Summary DateRangeRow —
              this card keeps only what the summary doesn't cover: campaign window +
              priority. Avoids a dead read-only duplicate of the same two dates. */}
          {(formData.custom_campaign_start || formData.custom_campaign_end) && (
            <KeyRow label={pick(lang, "ช่วงแคมเปญ", "Campaign window")}>
              <span className="text-sm">
                {formData.custom_campaign_start ? formatReadableDate(formData.custom_campaign_start) : "—"}
                {" → "}
                {formData.custom_campaign_end ? formatReadableDate(formData.custom_campaign_end) : "—"}
              </span>
            </KeyRow>
          )}
          <KeyRow label={pick(lang, "ลำดับความสำคัญ", "Priority")}>
            {formData.priority !== undefined && formData.priority !== null && formData.priority !== "" ? (
              formData.priority
            ) : (
              <EmptyValue />
            )}
          </KeyRow>
        </div>
      </div>
    </div>
  );
}
